// 모비온(인라이플 원태그, Enliple OneTag) 전환 트래킹 유틸.
// 기본 스크립트(tcs.js)와 CallMtm/callDM 스텁은 index.html <head>에 정적 설치돼 있고,
// 초기 로드 방문은 tcs.js가 자동 발화한다. SPA 내부 이벤트는 OneTag SPA 가이드에 따라
// 아래 함수로 발화한다: 페이지 이동=callDM(), 가입/로그인=TCS.sendMember(),
// 간편결제=TCS.simplePay(), 로그아웃=TCS.logout(). (상품수집 리타겟팅은 ENP_VAR+CallMtm)

/** SNS 로그인/가입 코드 (OneTag 가이드) */
export const MOBON_SNS_CODE = {
  KAKAO: '00001',
  NAVER: '00002',
  FACEBOOK: '00003',
  INSTAGRAM: '00004',
  /** 기타 (일반 가입/로그인 포함) */
  ETC: '00000',
} as const;

/** 간편결제 코드 (OneTag 가이드) */
export const MOBON_PAY_CODE = {
  KAKAO: '10001',
  NAVER: '10002',
  PAYCO: '10005',
  /** 기타 (사이트 일반결제 포함) */
  ETC: '19000',
} as const;

type MobonConvType = 'etc' | 'join' | 'login';

type CallMtmArgs = {
  productName?: string;
  convType?: MobonConvType;
  /** 클릭 전환용 CSS selector */
  click?: string;
};

type MobonCollect = {
  productCode: string;
  productName: string;
  price?: string;
  dcPrice?: string;
  soldOut?: string;
  imageUrl?: string;
  topCategory?: string;
};

type EnpVar = {
  collect?: MobonCollect;
  [key: string]: unknown;
};

/** TCS.sendMember 매개변수 (OneTag 가이드) */
type TcsMember = {
  /** 가입/로그인 구분 */
  type: 'join' | 'login';
  /** SNS 로그인/가입 코드 (MOBON_SNS_CODE) */
  snsCode: string;
  memberId: string;
  memberName?: string;
  email?: string;
  phoneNumber?: string;
  /** 성별: 'F' | 'M' */
  gender?: string;
  /** 문자 수신 여부: 'Y' | 'N' */
  smsAllowed?: string;
  /** 이메일 수신 여부: 'Y' | 'N' */
  emailAllowed?: string;
};

/** TCS.simplePay 상품 항목 (OneTag 가이드) */
type TcsPayProduct = {
  productId: string;
  productName: string;
  productPrice: string;
  productDcPrice?: string;
  productQty: string;
};

/** TCS.simplePay 매개변수 (OneTag 가이드) */
type TcsSimplePay = {
  /** 간편결제 코드 (MOBON_PAY_CODE) */
  payCode: string;
  orderId: string;
  products: TcsPayProduct[];
  totalPrice: string;
  totalQty: string;
  memberId?: string;
};

declare global {
  interface Window {
    CallMtm?: { (args?: CallMtmArgs): void; q?: unknown[] };
    /** 원태그 SPA 페이지 이동 수집 API(화면 전환 시 호출). index.html에 스텁 설치. */
    callDM?: { (...args: unknown[]): void; q?: unknown[] };
    /** 원태그(tcs.js) SPA API. 가입/로그인·간편결제·로그아웃 발화. */
    TCS?: {
      logout?: () => void;
      sendMember?: (member: TcsMember) => void;
      simplePay?: (order: TcsSimplePay) => void;
    };
    ENP_VAR?: EnpVar;
  }
}

/**
 * 방문(공통) — SPA 라우터 변경 시 호출. 초기 로드는 tcs.js/스텁이 자동 발화하므로 호출부에서 제외한다.
 * 원태그(OneTag) SPA 가이드상 페이지 이동 데이터는 callDM()으로 수집한다.
 */
export const mobonTrackPageView = () => {
  if (typeof window === 'undefined') return;
  window.CallMtm?.();
  window.callDM?.();
};

/** 로그아웃 — OneTag 가이드: TCS.logout(). */
export const mobonTrackLogout = () => {
  if (typeof window === 'undefined') return;
  window.TCS?.logout?.();
};

/**
 * 가입/로그인 완료 — OneTag 가이드: TCS.sendMember(JsonObject).
 * 소셜 로그인 리다이렉트에서 호출한다.
 */
export const mobonTrackMember = (member: TcsMember) => {
  if (typeof window === 'undefined') return;
  window.TCS?.sendMember?.(member);
};

/**
 * 상품(차량) 조회 수집 — 다이나믹 리타겟팅용. ENP_VAR.collect 세팅 후 CallMtm 호출.
 * 차량 상세 페이지가 없어 "차량 클릭(예약 진입)" 시점에 수집한다.
 */
export const mobonTrackProductView = ({
  productCode,
  productName,
  price,
  dcPrice,
  imageUrl,
  topCategory,
}: {
  productCode: string;
  productName: string;
  /** 정가(원가) */
  price?: number;
  /** 할인가(실판매가) */
  dcPrice?: number;
  imageUrl?: string;
  topCategory?: string;
}) => {
  if (typeof window === 'undefined') return;
  const collect: MobonCollect = { productCode, productName, soldOut: 'N' };
  if (price != null) collect.price = String(price);
  if (dcPrice != null) collect.dcPrice = String(dcPrice);
  if (imageUrl) collect.imageUrl = imageUrl;
  if (topCategory) collect.topCategory = topCategory;
  window.ENP_VAR = { ...window.ENP_VAR, collect };
  window.CallMtm?.();
};

/**
 * 간편결제 완료 — OneTag 가이드: TCS.simplePay(JsonObject).
 * 결제 완료 시 주문/상품 정보를 함께 전달한다.
 */
export const mobonTrackPurchase = ({
  orderId,
  totalPrice,
  productId,
  productName = '렌터카 예약',
  productDcPrice,
  memberId,
  payCode = MOBON_PAY_CODE.ETC,
}: {
  orderId: string;
  totalPrice: number;
  /** 상품(차량) 코드. 없으면 orderId로 대체. */
  productId?: string;
  productName?: string;
  /** 할인가(있을 때) */
  productDcPrice?: number;
  memberId?: string;
  payCode?: string;
}) => {
  if (typeof window === 'undefined') return;
  const priceStr = String(totalPrice);
  window.TCS?.simplePay?.({
    payCode,
    orderId,
    products: [
      {
        productId: productId ?? orderId,
        productName,
        productPrice: priceStr,
        ...(productDcPrice != null && {
          productDcPrice: String(productDcPrice),
        }),
        productQty: '1',
      },
    ],
    totalPrice: priceStr,
    totalQty: '1',
    ...(memberId && { memberId }),
  });
};
